import { useEffect, useRef, type KeyboardEvent } from 'react';
import { RailArrow, useRail } from '../../shared/rail.js';
import { CarouselDots } from '../results/offer/offer_carousel.js';
import { attributesDeclared, recommendedFare } from './fare_ladder.js';
import type { FlightFareSource } from './fare_source.js';
import type { FaresText } from './fares_copy.js';
import { captionOf, FareFamilyCard } from './fare_family_card.js';
// A folha vem com o trilho, e não só com a página: a expansão inline
// (`FareExpansion`) monta o trilho fora da página, e sem esta importação os
// cards chegariam lá sem estilo. Importar a mesma folha em dois lugares é
// idempotente no bundler.
import './fares_page.css';

/**
 * O trilho HORIZONTAL de famílias de uma perna — um `radiogroup`, N cards,
 * rolagem com snap e as setas do dock de hotel (`RailArrow`, a mesma medição e
 * o mesmo passo de ~85% da janela).
 *
 * As famílias ficam alinhadas à ESQUERDA e transbordam para a direita: o frame
 * não centraliza, porque o card mais barato é a âncora de leitura e centrar faria
 * a primeira família mudar de lugar conforme a companhia oferece três ou cinco.
 *
 * A recomendada sai do `recommendedFare` (a mais barata COM despacho). Nunca
 * prosa de LLM, e nunca a do provider.
 *
 * **O teclado é implementado aqui, não herdado.** `role="radio"` num `<button>`
 * NÃO ganha setas do browser — quem escreve o padrão de grupo de rádio é a
 * aplicação. Então: um único ponto de tabulação (roving `tabIndex`, o mesmo
 * mecanismo da grade do `PlannerForm`), setas movendo foco E seleção, `Home`/`End`
 * nas pontas, e a volta ao começo no fim do trilho. Sem isso o grupo prometia um
 * contrato que não cumpria: cada card virava um ponto de tabulação e Tab atravessava
 * cinco tarifas para sair de um controle só.
 *
 * Os rótulos das setas (`flightFares.railPrevious`/`railNext`) recebem
 * `{ group: label }`; o jsdom nunca renderiza as setas (`useRail` só as mostra
 * em overflow), então um placeholder errado aqui não derruba teste nenhum —
 * confira a cópia à mão ao mexer aqui.
 */
export function FareRail({
  label,
  fares,
  selected,
  readOnly,
  nav = 'arrows',
  onPick,
  text,
}: {
  /** Nome acessível JÁ resolvido do grupo — também entra no rótulo das setas.
   *  A página compõe "Tarifas · Ida"; a expansão inline manda "Ida · LATAM",
   *  "Trecho 2" ou "Tarifas · ida + volta" (ver `ladder_labels.ts`). O trilho
   *  não decide rótulo: quem conhece a perna e a companhia é quem monta. */
  readonly label: string;
  readonly fares: readonly FlightFareSource[];
  readonly selected: string | undefined;
  readonly readOnly?: boolean;
  /** Como se anda no trilho quando ele transborda: as setas do dock (padrão,
   *  o desenho largo) ou os pontos sob ele (a tela do celular, #3875 — no
   *  toque a seta cobre o card, e os pontos são o aviso de que há mais). */
  readonly nav?: 'arrows' | 'dots';
  readonly onPick: (fareId: string) => void;
  readonly text: FaresText;
}) {
  const { copy } = text;
  const { listRef, available, measure, scroll } = useRail<HTMLDivElement>();
  const recommended = recommendedFare(fares, text.classify)?.fareId;
  // Ordem de PREÇO: a comparação horizontal só é honesta se o eixo for o mesmo
  // em todas as pernas — a ordem do provider varia por perna.
  const ordered = [...fares].sort((a, b) => a.price.amount - b.price.amount);
  // UMA legenda por trilho, não uma por card (validação de 30/09): quando
  // todas as famílias declaram atributos E vêm da MESMA fonte, "Regras
  // informadas pelo fornecedor" repetida cinco vezes é ruído — o trilho a diz
  // uma vez, sob os cards, e cada card cala a sua. Fontes mistas ou um card
  // sem atributos (que mostra o texto cru do fornecedor) voltam ao desenho por
  // card: aí a legenda é informação, porque muda de um para o outro.
  const sources = new Set(ordered.map((f) => f.attributes?.source));
  const [onlySource] = sources;
  const railCaption =
    onlySource && sources.size === 1 && ordered.every(attributesDeclared)
      ? captionOf(onlySource, copy)
      : undefined;
  // O card que carrega o ponto de tabulação: o escolhido, ou o primeiro enquanto
  // nada foi escolhido — nunca nenhum, senão o grupo sai do alcance do Tab.
  const activeIndex = Math.max(
    0,
    ordered.findIndex((fare) => fare.fareId === selected),
  );

  // No celular o trilho ABRE com a família escolhida à vista (#3875): o
  // default (`defaultSel`) é a mais barata — a primeira do trilho —, mas a
  // escada também reabre numa tarifa já decidida (a semente do multi-trecho, a
  // escolha que a pessoa fez antes), e abrir com ela cortada na borda direita
  // escondia justamente o que a página quer mostrar. Só na montagem: depois
  // disso quem rola é a pessoa (e o teclado, que já foca o card). `start` e
  // não `center`: o snap é `start`, e o `scroll-padding` já dá o respiro. A
  // ref segura a montagem: as deps re-entram no efeito, só a primeira rodada passa.
  const openedRef = useRef(false);
  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    if (nav !== 'dots' || !selected) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-fare="${CSS.escape(selected)}"]`)
      ?.scrollIntoView({ behavior: 'instant', inline: 'start', block: 'nearest' });
  }, [nav, selected, listRef]);

  /** Setas movem foco E seleção (é o que um grupo de rádio faz — não há "focado
   *  mas não escolhido"), e dão a volta nas pontas. O foco vai pelo DOM, na ordem
   *  em que os cards foram renderizados: o trilho rola sozinho atrás dele. */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    let next: number | undefined;
    if (step !== undefined) next = (activeIndex + step + ordered.length) % ordered.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = ordered.length - 1;
    const fare = next === undefined ? undefined : ordered[next];
    if (!fare || readOnly) return;
    event.preventDefault();
    onPick(fare.fareId);
    listRef.current?.querySelectorAll<HTMLElement>('[data-fare]')[next ?? 0]?.focus();
  };

  return (
    <div className="ffs-rail">
      {/* `<div role="radiogroup">` e não `<ul>`: `radiogroup` só admite `radio`
          como filho, e os `listitem` de uma lista de verdade quebrariam a
          navegação por setas do grupo. */}
      <div
        ref={listRef}
        // Com pontos, a barra de rolagem some (a régua das abas e da galeria do
        // hotel): os pontos já dizem que há mais, e no toque a barra é só ruído
        // entre o trilho e eles. Com setas ela fica — é o desktop, onde a barra
        // é o meio de quem rola com a roda.
        className={`ffs-rail-list${nav === 'dots' ? ' [scrollbar-width:none] [&::-webkit-scrollbar]:hidden' : ''}`}
        role="radiogroup"
        aria-label={label}
        // Focável por script (quem escuta as setas precisa ser), mas fora do
        // Tab: o único ponto de tabulação é o radio do roving.
        tabIndex={-1}
        onScroll={measure}
        onKeyDown={onKeyDown}
      >
        {ordered.map((fare, i) => (
          <FareFamilyCard
            key={fare.fareId}
            fare={fare}
            selected={selected === fare.fareId}
            recommended={recommended === fare.fareId}
            roving={i === activeIndex}
            {...(readOnly ? { readOnly } : {})}
            {...(railCaption ? { caption: false } : {})}
            onPick={() => {
              onPick(fare.fareId);
            }}
            text={text}
          />
        ))}
      </div>
      {railCaption && <p className="ffs-source ffs-rail-source">{railCaption}</p>}
      {nav === 'arrows' && available.previous && (
        <RailArrow
          direction="previous"
          label={copy.railPrevious(label)}
          onClick={() => {
            scroll(-1);
          }}
        />
      )}
      {nav === 'arrows' && available.next && (
        <RailArrow
          direction="next"
          label={copy.railNext(label)}
          onClick={() => {
            scroll(1);
          }}
        />
      )}
      {nav === 'dots' && (
        <CarouselDots railRef={listRef} count={ordered.length} slideLabel={copy.slide} />
      )}
    </div>
  );
}
